import { useState, type ReactNode } from "react";
import type { CadInstance } from "../../bimgo";
import type { HostsView } from "../../hooks/useHosts";
import type { PluginsController } from "../../hooks/usePlugins";
import { useLocale } from "../../i18n/LocaleProvider";
import { CATALOG, pluginView, type PluginId } from "../../lib/plugins";
import { PluginManageDialog } from "../plugins/manage";
import { Spinner } from "../settings/Spinner";
import { CheckIcon } from "../skills/icons";

const EMPTY: CadInstance[] = [];

/** First connection, in the place a new user already looks: above the composer. */
export function PluginSetupCard({ hosts, plugins, onOpenPlugins, children }: {
  hosts: HostsView;
  plugins: PluginsController;
  onOpenPlugins: () => void;
  /** The usual greeting once both plugins have been configured. */
  children: ReactNode;
}) {
  const { t } = useLocale();
  const [manageId, setManageId] = useState<PluginId | null>(null);
  const instances = hosts.kind === "loaded" ? hosts.instances : EMPTY;
  const snapshots = plugins.state.kind === "loaded" ? plugins.state.snapshots : null;
  const configured = new Set(CATALOG.filter((entry) => {
    const status = pluginView(snapshots, instances, entry.id)?.status;
    return status === "installed" || status === "running";
  }).map((entry) => entry.id));
  const allConfigured = CATALOG.every((entry) => configured.has(entry.id));
  const hasUpdates = CATALOG.some((entry) => entry.available
    && pluginView(snapshots, instances, entry.id)?.updateAvailable);
  const checking = plugins.state.kind === "loading" || hosts.kind === "loading";
  const launchHosts = CATALOG.filter((entry) => configured.has(entry.id));
  // Both integrations must be configured before prompting the user to launch CAD.
  // Only a successful host lookup can establish that nothing is connected.
  const showLaunch = manageId === null && !checking && hosts.kind === "loaded"
    && instances.length === 0 && allConfigured && !hasUpdates;
  const launchHostName = launchHosts.length === 2
    ? t("chat.pluginLaunch.eitherHost")
    : launchHosts[0]?.hostName ?? "";
  // Keep installation results visible until the user closes the dialog.
  const showSetup = manageId !== null || (!checking && !allConfigured);
  const showChecking = manageId === null && checking && !allConfigured;

  function setup(vendor: PluginId) {
    if (plugins.busy) return;
    if (plugins.state.kind === "unsupported") {
      onOpenPlugins();
      return;
    }
    setManageId(vendor);
  }

  return (
    <>
      {showLaunch ? (
        <>
          {children}
          <section className="plugin-launch" aria-labelledby="plugin-launch-title">
            <div className="plugin-launch-logos" aria-hidden="true">
              {launchHosts.map((entry) => (
                <img key={entry.id} src={`/integrations/${entry.id}-logo.png`} alt="" width="40" height="40" />
              ))}
            </div>
            <div className="plugin-launch-copy">
              <h2 id="plugin-launch-title">{t("chat.pluginLaunch.title", { host: launchHostName })}</h2>
              <p>{t("chat.pluginLaunch.description", { host: launchHostName })}</p>
            </div>
            <span className="plugin-launch-status" role="status">
              {hosts.refreshing ? <Spinner size={14} /> : <span className="plugin-launch-dot" aria-hidden="true" />}
              {t(hosts.refreshing ? "chat.pluginSetup.checking" : "chat.pluginLaunch.waiting")}
            </span>
          </section>
        </>
      ) : showChecking ? (
        <div className="plugin-setup-checking" role="status">
          <Spinner size={16} />
          {t("chat.pluginSetup.checking")}
        </div>
      ) : showSetup ? (
        <section className="plugin-setup" aria-labelledby="plugin-setup-title">
          <h2 id="plugin-setup-title">{t("chat.pluginSetup.title")}</h2>
          <p className="plugin-setup-description">{t("chat.pluginSetup.description")}</p>
          <div className="plugin-setup-logos" aria-hidden="true">
            <div className="plugin-setup-logo is-revit">
              <img src="/integrations/revit-logo.png" alt="" width="224" height="224" />
            </div>
            <div className="plugin-setup-logo is-bimgo">
              <img src="/bimgo-logo-symbol.svg" alt="" width="36" height="36" />
            </div>
            <div className="plugin-setup-logo is-autocad">
              <img src="/integrations/autocad-logo.png" alt="" width="224" height="224" />
            </div>
          </div>
          <div className="plugin-setup-actions">
            {checking ? (
              <span className="plugin-setup-checking" role="status">
                <Spinner size={16} />
                {t("chat.pluginSetup.checking")}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className={`plugin-setup-button${configured.has("revit") ? " is-configured" : ""}`}
                  disabled={plugins.busy || configured.has("revit")}
                  onClick={() => setup("revit")}
                >
                  {configured.has("revit") ? <CheckIcon size={16} /> : null}
                  {configured.has("revit") ? t("chat.pluginSetup.configured", { host: "Revit" }) : t("chat.pluginSetup.revit")}
                </button>
                <button
                  type="button"
                  className={`plugin-setup-button${configured.has("autocad") ? " is-configured" : ""}`}
                  disabled={plugins.busy || configured.has("autocad")}
                  onClick={() => setup("autocad")}
                >
                  {configured.has("autocad") ? <CheckIcon size={16} /> : null}
                  {configured.has("autocad") ? t("chat.pluginSetup.configured", { host: "AutoCAD" }) : t("chat.pluginSetup.autocad")}
                </button>
              </>
            )}
          </div>
        </section>
      ) : children}
      <PluginManageDialog
        open={manageId !== null}
        onClose={() => setManageId(null)}
        vendor={manageId ?? "revit"}
        plugins={plugins}
        instances={instances}
      />
    </>
  );
}
